// =====================================================================================
// INVRT Radar — web console — client-side CSV
//
// Revealed contacts are already in the browser after a reveal, so downloading them costs
// nothing and needs no second API call. The ledger's own CSV comes from the SERVER
// (GET /v1/ledger/export?format=csv), because those figures are the cost-basis record and
// must be the ones Postgres computed — this module is for rows the console already holds.
// =====================================================================================

/** RFC 4180: quote a field, and double any quote inside it. */
function escapeField(value: unknown): string {
  if (value === null || value === undefined) return "";
  const text = typeof value === "string" ? value : String(value);
  if (/[",\r\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
}

export interface CsvColumn<Row> {
  header: string;
  value: (row: Row) => unknown;
}

export function toCsv<Row>(rows: Row[], columns: Array<CsvColumn<Row>>): string {
  const head = columns.map((column) => escapeField(column.header)).join(",");
  const body = rows.map((row) =>
    columns.map((column) => escapeField(column.value(row))).join(","),
  );
  return [head, ...body].join("\r\n");
}

/** Hands a string to the browser as a download. No-op outside a document context. */
export function downloadTextFile(filename: string, contents: string, mimeType = "text/csv"): void {
  if (typeof document === "undefined") return;
  const blob = new Blob([contents], { type: `${mimeType};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

/** A filename stem that sorts by date and never collides across organisations. */
export function timestampedFilename(stem: string, extension = "csv"): string {
  const now = new Date();
  const stamp = [
    now.getUTCFullYear(),
    String(now.getUTCMonth() + 1).padStart(2, "0"),
    String(now.getUTCDate()).padStart(2, "0"),
    "-",
    String(now.getUTCHours()).padStart(2, "0"),
    String(now.getUTCMinutes()).padStart(2, "0"),
  ].join("");
  return `${stem}-${stamp}.${extension}`;
}
